import { useState } from 'react'
import { Area, AreaChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { nodes as api } from '@/api/endpoints'
import type { UsageTimeframe } from '@/api/types'
import { Err } from '@/components/Err'
import { ExpertLine } from '@/components/Expert'
import { Button } from '@/components/ui/button'
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart'
import { usePoll } from '@/hooks/usePoll'
import { useFallback } from '@/hooks/useEventStream'
import { exact, sizeNumber, sizeUsage } from '@/ui/format'
import { TIMEFRAMES, axisWidth, gpuChartRows, lastMeasured, mbps, ramAxis, ramCeiling, usageRows, type GpuLine, type UsageRow } from './nodeUsage'

// 노드의 CPU·RAM 사용량 시계열. PVE RRD 를 요청 시점에 읽으므로 heartbeat 와 무관하게 polling 만 한다.
// 클러스터가 닿지 않던 구간은 선이 끊긴다 — 0 으로 그리지 않는다.

const USAGE_POLL_MS = 30000

const CONFIG = {
  cpu: { label: 'CPU', color: 'var(--chart-1)' },
  ram: { label: '메모리', color: 'var(--chart-2)' },
} satisfies ChartConfig

// GPU 그래프는 이 노드의 GPU 가 붙은 VM 이 있을 때만 있다. 선은 VM 이 아니라 노드의 GPU 하나씩이고,
// 우측 위의 토글로 켠 GPU 들이 한 그래프에 겹친다. 사용률은 게스트 수집기가 보고하기 전까지 비어 있다.
const GPU_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)']

export function NodeUsageCharts({ nodeId, gpus = [] }: { nodeId: string; gpus?: GpuLine[] }) {
  const [timeframe, setTimeframe] = useState<UsageTimeframe>('hour')
  const [gpuOff, setGpuOff] = useState<string[]>([])
  const activeGpus = gpus.filter((g) => !gpuOff.includes(g.id))
  const usage = usePoll(() => api.usage(nodeId, timeframe), useFallback(USAGE_POLL_MS), [nodeId, timeframe])
  const rows = usageRows(usage.data)
  const last = lastMeasured(rows)
  const ceiling = ramCeiling(rows)
  const ram = ramAxis(ceiling)

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[12px] text-muted-foreground">사용량</span>
        <div role="group" aria-label="기간" className="flex gap-1">
          {TIMEFRAMES.map((t) => (
            <Button
              key={t.value}
              size="sm"
              variant={t.value === timeframe ? 'default' : 'outline'}
              aria-pressed={t.value === timeframe}
              className="h-7 px-2 text-[12px]"
              onClick={() => setTimeframe(t.value)}
            >
              {t.word}
            </Button>
          ))}
        </div>
      </div>
      <div className="mt-2 empty:hidden">
        <Err error={usage.error} />
      </div>
      {rows.length === 0 ? (
        <div className="mt-2 text-[12px] text-muted-foreground">{usage.loading ? '불러오는 중' : '측정값 없음'}</div>
      ) : (
        <div className="mt-2 grid gap-3">
          <UsageChart
            title="CPU"
            headline={last?.cpu !== null && last?.cpu !== undefined ? `${last.cpu}%` : '—'}
            dataKey="cpu"
            rows={rows}
            domain={[0, 100]}
            tick={(v) => String(Math.round(v))}
            tip={(v) => `${v}%`}
          />
          <UsageChart
            title="메모리"
            headline={last?.ram !== null && last?.ram !== undefined ? sizeUsage(last.ram, last.ramTotal) : '—'}
            dataKey="ram"
            rows={rows}
            domain={[0, ceiling ?? 'auto']}
            ticks={ram.ticks}
            tick={(v) => sizeNumber(v, ram.unit)}
            tip={(v) => `${sizeNumber(v, ram.unit)}${ram.unit}`}
          />
          {gpus.length > 0 ? (
            <GpuChart
              rows={rows}
              gpus={gpus}
              active={activeGpus}
              onToggle={(id) => setGpuOff((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))}
            />
          ) : null}
        </div>
      )}
      <ExpertLine
        className="mt-2"
        items={[
          last ? `마지막 측정 ${exact(last.time)}` : null,
          last && (last.netIn !== null || last.netOut !== null) ? `수신 ${mbps(last.netIn)} · 송신 ${mbps(last.netOut)}` : null,
        ]}
      />
    </div>
  )
}

function UsageChart({
  title,
  headline,
  dataKey,
  rows,
  domain,
  ticks,
  tick,
  tip,
}: {
  title: string
  headline: string
  dataKey: 'cpu' | 'ram'
  rows: UsageRow[]
  domain: [number, number | 'auto']
  ticks?: number[] | undefined
  /** 눈금의 글자(단위 없이)와 툴팁의 글자(단위와 함께). */
  tick: (v: number) => string
  tip: (v: number) => string
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between text-[12px]">
        <span className="text-muted-foreground">{title}</span>
        <span className="font-medium tabular-nums">{headline}</span>
      </div>
      <ChartContainer config={CONFIG} className="mt-1 aspect-auto h-24 w-full">
        <AreaChart data={rows} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={28} tick={{ fontSize: 10 }} />
          <YAxis
            domain={domain}
            width={axisWidth(typeof domain[1] === 'number' ? tick(domain[1]) : undefined)}
            allowDecimals={false}
            {...(ticks ? { ticks } : {})}
            tickFormatter={tick}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10 }}
          />
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent indicator="line" formatter={(value) => tip(Number(value))} />}
          />
          <Area
            dataKey={dataKey}
            type="monotone"
            stroke={`var(--color-${dataKey})`}
            fill={`var(--color-${dataKey})`}
            fillOpacity={0.15}
            strokeWidth={1.5}
            connectNulls={false}
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ChartContainer>
    </div>
  )
}

function GpuChart({
  rows,
  gpus,
  active,
  onToggle,
}: {
  rows: UsageRow[]
  gpus: GpuLine[]
  active: GpuLine[]
  onToggle: (id: string) => void
}) {
  const data = gpuChartRows(rows, active)
  const measured = rows.some((r) => active.some((g) => r.gpus[g.id] !== undefined && r.gpus[g.id] !== null))
  const config = Object.fromEntries(
    active.map((g, i) => [`g${i}`, { label: g.label, color: GPU_COLORS[i % GPU_COLORS.length]! }]),
  ) satisfies ChartConfig
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-[12px]">
        <span className="text-muted-foreground">GPU 사용률</span>
        <div role="group" aria-label="GPU" className="flex flex-wrap justify-end gap-1">
          {gpus.map((g) => {
            const on = active.some((a) => a.id === g.id)
            const index = active.findIndex((a) => a.id === g.id)
            return (
              <Button
                key={g.id}
                size="sm"
                variant={on ? 'default' : 'outline'}
                aria-pressed={on}
                className="h-6 px-2 text-[11px]"
                style={on ? { backgroundColor: GPU_COLORS[index % GPU_COLORS.length] } : undefined}
                onClick={() => onToggle(g.id)}
              >
                {g.label}
              </Button>
            )
          })}
        </div>
      </div>
      {active.length === 0 ? (
        <div className="mt-1 text-[12px] text-muted-foreground">켠 GPU 없음</div>
      ) : !measured ? (
        <div className="mt-1 text-[12px] text-muted-foreground">측정값 없음</div>
      ) : (
        <ChartContainer config={config} className="mt-1 aspect-auto h-24 w-full">
          <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={28} tick={{ fontSize: 10 }} />
            <YAxis domain={[0, 100]} width={30} tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="line" formatter={(value) => `${value}%`} />} />
            {active.map((g, i) => (
              <Line
                key={g.id}
                dataKey={`g${i}`}
                type="monotone"
                stroke={`var(--color-g${i})`}
                strokeWidth={1.5}
                connectNulls={false}
                isAnimationActive={false}
                dot={false}
              />
            ))}
          </LineChart>
        </ChartContainer>
      )}
    </div>
  )
}
